import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import type { PremiseConfig } from "@shared/types/foyer";

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}

/**
 * Manage the per-premise `demoIpRanges`. TVs pairing from a matching
 * IPv4 CIDR bypass the license-seat cap and get tagged `demo:true` so
 * they're visibly separate in the Devices view.
 *
 * Saving the premise (top-bar Save) persists the list. Validation is
 * intentionally loose - bad entries just don't match anything.
 */
export default function DemoIpRangesEditor({ premise, onChange }: Props) {
  const [draft, setDraft] = useState("");
  const ranges = premise.demoIpRanges ?? [];

  function add() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (ranges.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange({ ...premise, demoIpRanges: [...ranges, trimmed] });
    setDraft("");
  }

  function remove(idx: number) {
    onChange({
      ...premise,
      demoIpRanges: ranges.filter((_, i) => i !== idx),
    });
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Demo IP ranges
      </Typography>

      <Stack spacing={1} sx={{ mb: 2 }}>
        {ranges.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
          >
            No demo ranges configured.
          </Typography>
        )}
        {ranges.map((r, i) => (
          <Stack
            key={`${r}-${i}`}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
            data-testid={`demo-range-row-${i}`}
          >
            <Chip
              size="small"
              label={r}
              sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
            />
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Remove">
              <IconButton
                size="small"
                aria-label={`Remove range ${r}`}
                onClick={() => remove(i)}
                data-testid={`demo-range-remove-${i}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1}>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="10.0.0.0/24 or 192.168.1.5"
          size="small"
          sx={{ flex: 1 }}
          inputProps={{
            "data-testid": "demo-range-input",
            style: { fontFamily: "ui-monospace, Menlo, monospace" },
          }}
        />
        <Button
          variant="outlined"
          onClick={add}
          startIcon={<AddIcon />}
          disabled={!draft.trim()}
          data-testid="demo-range-add"
        >
          Add
        </Button>
      </Stack>
    </Paper>
  );
}
